import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useToast } from '../components/ToastContainer';
import { useAuth } from '../contexts/AuthContext';
import { Notification } from '../components/NotificationsDropdown';
import './NotificationsPage.css';

const NotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 20;

  // Initialize filters from URL params
  const [filterUnread, setFilterUnread] = useState(() => {
    return searchParams.get('unread') === 'true';
  });
  const [filterCategory, setFilterCategory] = useState<string>(() => {
    return searchParams.get('category') || 'all';
  });
  
  // Track if this is the initial mount
  const isInitialMount = useRef(true);
  // Track previous searchParams to detect browser navigation
  const prevSearchParamsRef = useRef<string>(searchParams.toString());

  // Sync state FROM URL params when they change (browser back/forward)
  useEffect(() => {
    const currentParamsString = searchParams.toString();
    const prevParamsString = prevSearchParamsRef.current;
    
    // Only sync if URL params actually changed (browser navigation)
    if (currentParamsString !== prevParamsString) {
      prevSearchParamsRef.current = currentParamsString;
      
      const urlUnread = searchParams.get('unread') === 'true';
      const urlCategory = searchParams.get('category') || 'all';
      
      // Only update state if it differs from URL (browser back/forward navigation)
      if (urlUnread !== filterUnread) {
        setFilterUnread(urlUnread);
      }
      if (urlCategory !== filterCategory) {
        setFilterCategory(urlCategory);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Sync URL params when filters change (user actions)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSearchParamsRef.current = searchParams.toString();
      return;
    }

    const params = new URLSearchParams();
    if (filterUnread) params.set('unread', 'true');
    if (filterCategory !== 'all') params.set('category', filterCategory);
    
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    
    // Only update if params actually changed
    if (currentParamsString !== newParamsString) {
      prevSearchParamsRef.current = newParamsString;
      setSearchParams(params, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUnread, filterCategory]);

  useEffect(() => {
    fetchNotifications();
  }, [page, filterUnread, filterCategory]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getNotifications({
        unreadOnly: filterUnread,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        category: filterCategory !== 'all' ? filterCategory : undefined
      });

      if (page === 1) {
        setNotifications(response.data.notifications || []);
      } else {
        setNotifications(prev => [...prev, ...(response.data.notifications || [])]);
      }

      setTotal(response.data.total || 0);
      setHasMore((response.data.notifications || []).length === pageSize);
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      showToast('Failed to fetch notifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await authAPI.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      showToast('Notification marked as read', 'success');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showToast('Failed to mark notification as read', 'error');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await authAPI.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      showToast('All notifications marked as read', 'success');
    } catch (error) {
      console.error('Error marking all as read:', error);
      showToast('Failed to mark all as read', 'error');
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleFilterChange = (unread: boolean, category: string) => {
    setFilterUnread(unread);
    setFilterCategory(category);
    setPage(1);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'error': return 'Error';
      case 'warning': return 'Warning';
      case 'success': return 'Success';
      case 'announcement': return 'Announcement';
      case 'system_update': return 'System';
      default: return 'Info';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1>Notifications</h1>
        <div className="notifications-actions">
          {unreadCount > 0 && (
            <button
              className="btn btn-secondary mark-all-read-btn"
              onClick={handleMarkAllAsRead}
            >
              Mark all as read ({unreadCount})
            </button>
          )}
        </div>
      </div>

      <div className="notifications-filters">
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filterUnread}
              onChange={(e) => handleFilterChange(e.target.checked, filterCategory)}
            />
            Unread only
          </label>
        </div>
        <div className="filter-group">
          <label>Category:</label>
          <select
            value={filterCategory}
            onChange={(e) => handleFilterChange(filterUnread, e.target.value)}
          >
            <option value="all">All</option>
            <option value="message">Messages</option>
            <option value="system_update">System Updates</option>
            <option value="quiz">Quiz</option>
            <option value="account">Account</option>
          </select>
        </div>
        <div className="notifications-count">
          Showing {notifications.length} of {total} notifications
        </div>
      </div>

      {loading && notifications.length === 0 ? (
        <div className="notifications-loading">Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <div className="notifications-empty">
          <p>No notifications found</p>
        </div>
      ) : (
        <>
          <div className="notifications-list">
            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-card ${notification.isRead ? 'read' : 'unread'}`}
              >
                <div className="notification-card-header">
                  <div className={`notification-type-badge type-${notification.type}`}>
                    <span className="notification-type-text">{getTypeLabel(notification.type)}</span>
                  </div>
                  <div className="notification-meta">
                    {notification.priority !== 'medium' && (
                      <span className={`notification-priority-badge priority-${notification.priority}`}>
                        {notification.priority}
                      </span>
                    )}
                    {notification.category === 'system_update' && (
                      <span className="notification-category-badge">System</span>
                    )}
                    {!notification.isRead && (
                      <span className="notification-unread-indicator">New</span>
                    )}
                  </div>
                </div>

                {notification.title && (
                  <h3 className="notification-card-title">{notification.title}</h3>
                )}

                <div className="notification-card-message">{notification.message}</div>

                {notification.metadata && Object.keys(notification.metadata).length > 0 && (
                  <div className="notification-metadata">
                    {notification.metadata.version && (
                      <span className="metadata-item">Version: {notification.metadata.version}</span>
                    )}
                    {notification.metadata.featureName && (
                      <span className="metadata-item">Feature: {notification.metadata.featureName}</span>
                    )}
                  </div>
                )}

                <div className="notification-card-footer">
                  <span className="notification-time">{formatTime(notification.createdAt)}</span>
                  {notification.expiresAt && (
                    <span className="notification-expires">
                      Expires {formatTime(notification.expiresAt)}
                    </span>
                  )}
                  {!notification.isRead && (
                    <button
                      className="mark-read-link"
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="notifications-load-more">
              <button
                className="btn btn-primary"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsPage;

